import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import "./discover.css";
import { useState, useEffect, useContext, useCallback, useRef } from "react";
import { ChevronDown, ChevronRight, Compass, MapPin, Monitor, Navigation, RotateCw, SearchX, Sparkles, Star, House, WifiOff } from "lucide-react";
import generalRequest from "../../request/generalRequest";
import { CombinedContext } from "../../context/combinedContext";
import BookingModal from "../bookingModal/bookingModal";
import translations from "../../translations/translations";
import DiscoverSkeleton from "./discoverSkeleton/discoverSkeleton";
import CATEGORY_OPTIONS_SEARCH from "../../constants/categoryOptionsSearch";

const DEFAULT_COORDS = { lat: 18.4861, lng: -69.9312 }; // Santo Domingo

/* One page of results. The list starts here and grows by this much every time
   you ask for more, instead of stopping dead at the first twenty. */
const PAGE_SIZE = 20;

const CURRENCY_SYMBOLS = { USD: "$", DOP: "RD$", EUR: "€", CAD: "C$", MXN: "MX$" };

/* Same shape as the shop page: "$25", not "USD 25.00" */
const formatPrice = (price, currency) => {
	const code = currency || "USD";
	const amount = Number(price ?? 0).toLocaleString("en-US");
	return CURRENCY_SYMBOLS[code] ? `${CURRENCY_SYMBOLS[code]}${amount}` : `${code} ${amount}`;
};

const formatDistance = (km) => {
	const value = Number(km);
	if (!Number.isFinite(value) || value < 0) return null;
	if (value < 1) return `${Math.max(50, Math.round((value * 1000) / 50) * 50)} m`;
	/* A decimal stops being useful — and starts being noise — past ~100km */
	if (value < 100) return `${value.toFixed(1)} km`;
	return `${Math.round(value).toLocaleString("en-US")} km`;
};

const getCategoryLabel = (mainCategory, subCategory, language) => {
	const parent = CATEGORY_OPTIONS_SEARCH.find((c) => c.value === mainCategory);
	if (!parent) return null;
	const sub = parent.subCategories?.find((s) => s.value === subCategory);
	const target = sub && sub.value !== "all" && sub.value !== parent.value ? sub : parent;
	return language === "es" ? target.labelES : target.labelEN;
};

const getImageUrl = (img) => {
	if (!img) return null;
	return img.startsWith("http") ? img : `https://${process.env.REACT_APP_IMAGES_BUCKET}.s3.${process.env.REACT_APP_APP_REGION}.amazonaws.com/${img}`;
};

const SORT_OPTIONS = [
	{ key: "recommended", labelKey: "recommended", Icon: Sparkles },
	{ key: "distance", labelKey: "distance", Icon: Navigation },
	{ key: "rating_high", labelKey: "highestRated", Icon: Star },
];

/* ============================================================
   CARD
   ============================================================ */
const ShopCard = ({ shop, t, onBook }) => {
	const href = `/shops/${shop.slug || shop.shopId}`;
	const name = shop.name || t.shop.untitled;

	const rating = Number(shop.rating || 0);
	const reviewCount = shop.totalReviews ?? shop.reviewCount ?? 0;
	const photo = getImageUrl(shop.logo || shop.images?.[0] || shop.coverImage);
	const distance = formatDistance(shop.distanceKm ?? shop.distance);

	/* Most addresses already carry the city — only append it when it's missing */
	const hasCity = shop.city && shop.address?.toLowerCase().includes(shop.city.toLowerCase());
	const addressLine = [shop.address, hasCity ? null : shop.city].filter(Boolean).join(", ");

	const services = Array.isArray(shop.services) ? shop.services : [];
	const visible = services.slice(0, 3);
	const hidden = services.length - visible.length;

	const prices = services.map((s) => Number(s.price)).filter((p) => Number.isFinite(p) && p > 0);
	const cheapest = prices.length ? Math.min(...prices) : null;

	return (
		<article className="sf-disc-card">
			<Link to={href} className="sf-disc-media" aria-label={name}>
				{photo ? (
					<img className="sf-disc-photo" src={photo} alt="" loading="lazy" />
				) : (
					<span className="sf-disc-photo-empty">
						<Compass size={28} strokeWidth={1.4} />
						<span>{t.card.noPhoto}</span>
					</span>
				)}

				<span className="sf-disc-scrim" aria-hidden="true" />

				{rating > 0 && (
					<span className="sf-disc-rating">
						<Star size={12} fill="currentColor" strokeWidth={0} />
						{rating.toFixed(1)}
						{reviewCount > 0 && <i>({reviewCount})</i>}
					</span>
				)}

				{distance && (
					<span className="sf-disc-distance">
						<Navigation size={11} strokeWidth={2.4} />
						{distance}
					</span>
				)}
			</Link>

			<div className="sf-disc-body">
				<Link to={href} className="sf-disc-name">
					{name}
				</Link>

				{addressLine && (
					<p className="sf-disc-addr">
						<MapPin size={13} strokeWidth={2} />
						<span>{addressLine}</span>
					</p>
				)}
{/* 
				{cheapest !== null && (
					<div className="sf-disc-meta">
						<span className="sf-disc-from">
							{t.card.from} <strong>{formatPrice(cheapest, services[0]?.currency)}</strong>
						</span>
					</div>
				)} */}

				{visible.length > 0 && (
					<div className="sf-disc-services">
						{visible.map((service, idx) => {
							const locationType = service.locationType || (service.isHomeService ? "home" : "in_shop");

							return (
								<div className="sf-disc-service" key={service?.serviceId || idx}>
									<div className="sf-disc-service-copy">
										<span className="sf-disc-service-name">
											<span>{service.name}</span>
											{locationType === "home" && (
												<em className="sf-disc-tag is-home">
													<House size={11} strokeWidth={2.2} />
													{t.card.home}
												</em>
											)}
											{locationType === "online" && (
												<em className="sf-disc-tag is-online">
													<Monitor size={11} strokeWidth={2.2} />
													{t.card.online}
												</em>
											)}
										</span>
										<span className="sf-disc-service-meta">
											<strong>{service.price ? formatPrice(service.price, service.currency) : "—"}</strong>
											{service.duration && (
												<>
													<em aria-hidden="true">·</em>
													{service.duration} min
												</>
											)}
										</span>
									</div>

									<button type="button" className="sf-disc-book" onClick={() => onBook(shop, service.serviceId)}>
										{t.services.book}
									</button>
								</div>
							);
						})}
					</div>
				)}
			</div>

			<Link to={href} className="sf-disc-foot">
				{hidden > 0 ? t.services.more(hidden) : t.card.viewShop}
				<ChevronRight size={15} strokeWidth={2.4} />
			</Link>
		</article>
	);
};

/* ============================================================
   PAGE
   ============================================================ */
const Discover = () => {
	// 📍 Browser location
	const [coords, setCoords] = useState(null);

	const [sortBy, setSortBy] = useState("rating_high");
	const [reloadKey, setReloadKey] = useState(0);

	const [error, setError] = useState(null);
	const [loading, setLoading] = useState(true);

	/* Pagination — page/total/hasMore all come back from the server */
	const [page, setPage] = useState(1);
	const [total, setTotal] = useState(0);
	const [hasMore, setHasMore] = useState(false);
	const [loadingMore, setLoadingMore] = useState(false);
	const [loadMoreFailed, setLoadMoreFailed] = useState(false);

	// 🔹 Booking modal state
	const [isBookingOpen, setIsBookingOpen] = useState(false);
	const [selectedShop, setSelectedShop] = useState(null);

	const sortsRef = useRef(null);
	const sortSettled = useRef(false);

	/* Only the newest request may write to the list. Changing category or sort
	   while a "load more" is still in flight would otherwise staple the old
	   filter's page 2 onto the new filter's page 1. */
	const requestIdRef = useRef(0);

	// 🔹 Context
	const { language, selectedService, setSelectedService, selectedstaff, setCategory, shopsData, setShopsData } = useContext(CombinedContext);

	const { mainCategory, subCategory } = useParams();
	const [searchParams] = useSearchParams();
	const navigate = useNavigate();

	const t = translations[language].discover;

	const shops = Array.isArray(shopsData) ? shopsData : [];
	/* First paint gets the skeleton; a re-sort keeps the cards and dims them */
	const isFirstLoad = loading && shops.length === 0;

	useEffect(() => {
		const q = searchParams.get("q");
		const loc = searchParams.get("loc");

		if (!q && !loc) return;

		async function searchFromParams() {
			try {
				setLoading(true);
				const data = await generalRequest.searchShops(q || "", 1, 20, loc || undefined);
				setShopsData(data.shops);
				setTotal(Array.isArray(data?.shops) ? data.shops.length : 0);
				/* Load more pages the discover feed, not the search, so don't
				   offer it over a result set it can't extend */
				setHasMore(false);
				setError(null);
			} catch (err) {
				console.error(err);
				setError(t.errorLoading);
			} finally {
				setLoading(false);
			}
		}

		searchFromParams();
	}, [searchParams]);

	/* ---------------- Category sync ---------------- */

	useEffect(() => {
		if (!mainCategory) {
			navigate("/barbershop/barbershop");
		} else {
			setCategory(subCategory || mainCategory);
		}
	}, [mainCategory, subCategory]);

	/* ---------------- Request browser location ---------------- */

	useEffect(() => {
		if (!navigator.geolocation) {
			setCoords(DEFAULT_COORDS);
			return;
		}

		navigator.geolocation.getCurrentPosition(
			(pos) => setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
			// User denied or error → fallback to Santo Domingo
			() => setCoords(DEFAULT_COORDS),
			{ enableHighAccuracy: true, timeout: 8000, maximumAge: 60_000 },
		);
	}, []);

	/* ---------------- Load shops ---------------- */

	const loadPage = useCallback(
		async (pageNumber, { append = false } = {}) => {
			const requestId = ++requestIdRef.current;

			if (append) {
				setLoadingMore(true);
				setLoadMoreFailed(false);
			} else {
				setLoading(true);
				/* A fresh filter abandons whatever the last "load more" was doing */
				setLoadingMore(false);
				setLoadMoreFailed(false);
			}

			try {
				const data = await generalRequest.discover({
					category: mainCategory,
					subCategory: subCategory || "all",
					sortBy,
					page: pageNumber,
					limit: PAGE_SIZE,
					lat: coords?.lat,
					lng: coords?.lng,
				});

				/* A newer request overtook this one — its results own the list */
				if (requestId !== requestIdRef.current) return;

				const incoming = Array.isArray(data?.shops) ? data.shops : [];

				setShopsData((prev) => {
					if (!append) return incoming;

					/* The server orders pages deterministically, but belt and
					   braces: the same shop must never render twice. */
					const existing = Array.isArray(prev) ? prev : [];
					const seen = new Set(existing.map((item) => item.shopId));

					return existing.concat(incoming.filter((item) => !seen.has(item.shopId)));
				});

				setPage(pageNumber);
				setTotal(Number.isFinite(Number(data?.total)) ? Number(data.total) : incoming.length);
				/* If the server ever stops sending the flag, a full page still
				   means "there is probably more" */
				setHasMore(typeof data?.hasMore === "boolean" ? data.hasMore : incoming.length === PAGE_SIZE);
				setError(null);
			} catch (err) {
				console.error(err);
				if (requestId !== requestIdRef.current) return;

				/* A failed "load more" keeps the shops already on screen — only
				   a failed first page has nothing left to show but the error. */
				if (append) setLoadMoreFailed(true);
				else setError(t.errorLoading);
			}

			if (requestId !== requestIdRef.current) return;

			if (append) setLoadingMore(false);
			else setLoading(false);
		},
		[mainCategory, subCategory, sortBy, coords, setShopsData, t.errorLoading],
	);

	/* Any change to the filters, the sort or the location starts over at page 1 */
	useEffect(() => {
		if (!coords) return;
		loadPage(1);
	}, [coords, loadPage, reloadKey]);

	/* The rail overflows at phone width and the default sort is the last chip —
	   keep whichever is active in view. Placed instantly on first paint,
	   animated only once the user starts changing it. */
	useEffect(() => {
		const active = sortsRef.current?.querySelector(".is-active");
		if (!active) return;
		active.scrollIntoView({ block: "nearest", inline: "center", behavior: sortSettled.current ? "smooth" : "auto" });
		sortSettled.current = true;
	}, [sortBy, isFirstLoad]);

	/* ---------------- Handlers ---------------- */

	const handleBookService = useCallback(
		(shop, serviceId) => {
			setSelectedService(serviceId);
			setSelectedShop(shop);
			setIsBookingOpen(true);
		},
		[setSelectedService],
	);

	const handleLoadMore = useCallback(() => {
		if (loading || loadingMore || !hasMore) return;
		loadPage(page + 1, { append: true });
	}, [loading, loadingMore, hasMore, page, loadPage]);

	const categoryLabel = getCategoryLabel(mainCategory, subCategory, language) || t.title;

	if (isFirstLoad && !error) return <DiscoverSkeleton />;

	return (
		<div className="sf-disc-root">
			<div className="sf-disc-page">
				{/* HEADER */}
				<header>
					{/* <span className="sf-disc-eyebrow">{t.eyebrow}</span> */}
					<h1 className="sf-disc-title">{categoryLabel}</h1>
					{!error && (
						<p className="sf-disc-sub">
							<Compass size={15} strokeWidth={2} />
							{t.results(total || shops.length)}
						</p>
					)}
				</header>

				{/* CONTROLS */}
				{!error && (
					<div className="sf-disc-controls">
						<div className="sf-disc-sorts" ref={sortsRef} role="group" aria-label={t.toolbar.sortBy}>
							{SORT_OPTIONS.map(({ key, labelKey, Icon }) => (
								<button
									key={key}
									type="button"
									className={`sf-disc-sort-chip${sortBy === key ? " is-active" : ""}`}
									aria-pressed={sortBy === key}
									onClick={() => setSortBy(key)}
								>
									<Icon size={13} strokeWidth={2.2} />
									{t.sort[labelKey]}
								</button>
							))}
						</div>

						<button type="button" className="sf-disc-mapbtn" onClick={() => navigate("/map")}>
							<MapPin size={14} strokeWidth={2.2} />
							{t.toolbar.showMap}
						</button>
					</div>
				)}

				{/* RE-SORTING — the list stays put instead of blanking out */}
				<div className={`sf-disc-toast${loading && !isFirstLoad ? " is-visible" : ""}`} aria-live="polite">
					<span>
						<i aria-hidden="true" />
						{t.updating}
					</span>
				</div>

				{/* RESULTS */}
				{error ? (
					<div className="sf-disc-state is-error">
						<span className="sf-disc-state-icon">
							<WifiOff size={26} strokeWidth={1.8} />
						</span>
						<h2 className="sf-disc-state-title">{error}</h2>
						<p className="sf-disc-state-hint">{t.errorHint}</p>
						<button type="button" className="sf-disc-state-btn" onClick={() => setReloadKey((n) => n + 1)}>
							<RotateCw size={15} strokeWidth={2.2} />
							{t.retry}
						</button>
					</div>
				) : shops.length === 0 ? (
					<div className="sf-disc-state">
						<span className="sf-disc-state-icon">
							<SearchX size={26} strokeWidth={1.8} />
						</span>
						<h2 className="sf-disc-state-title">{t.empty}</h2>
						<p className="sf-disc-state-hint">{t.emptyHint}</p>
						<button type="button" className="sf-disc-state-btn" onClick={() => navigate("/map")}>
							<MapPin size={15} strokeWidth={2.2} />
							{t.toolbar.showMap}
						</button>
					</div>
				) : (
					<>
						<section className={`sf-disc-results${loading ? " is-busy" : ""}`}>
							{shops.map((shop) => (
								<ShopCard key={shop.shopId} shop={shop} t={t} onBook={handleBookService} />
							))}
						</section>

						{/* LOAD MORE — the list grows a page at a time */}
						{hasMore && (
							<div className="sf-disc-more">
								{loadMoreFailed && <p className="sf-disc-more-error">{t.loadMoreError}</p>}

								<button type="button" className="sf-disc-more-btn" onClick={handleLoadMore} disabled={loadingMore}>
									{loadingMore ? <i className="sf-disc-more-spin" aria-hidden="true" /> : <ChevronDown size={16} strokeWidth={2.2} />}
									{loadingMore ? t.loadingMore : t.loadMore}
								</button>

								<p className="sf-disc-more-count">{t.showingOf(shops.length, total)}</p>
							</div>
						)}
					</>
				)}
			</div>

			{/* Booking Modal */}
			{isBookingOpen && selectedShop && (
				<BookingModal
					open={isBookingOpen}
					onClose={() => setIsBookingOpen(false)}
					availabilityId={selectedShop.shopId}
					shopData={selectedShop}
					initialServiceId={selectedService}
					initialstaffId={selectedstaff}
				/>
			)}
		</div>
	);
};

export default Discover;
